import { Avatar, Grid, Heading, Stack, Text, Box } from "@chakra-ui/react";
import Joi from "joi";

import AnimatedInput from "components/Chakra/AnimatedInput";
import Button from "components/Chakra/Button";
import CustomWrapper from "components/Chakra/CustomWrapper";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import PhoneInput, { JoiPhoneValidator } from "components/Chakra/PhoneInput";
import CustomLink from "components/Chakra/CustomLink";
import useHelperCRM from "hooks/useHelperCRM";
import { useAlertModal } from "modals/useAlertModal";
import { useInfoModal } from "modals/useInfoModal";
import LocationSuggest from "components/Chakra/LocationSuggest";

type IForm = {
  phone: { number: string; prefix: string };
  email: string;
  country: string;
};

const formSchema = Joi.object({
  email: Joi.string().email({ tlds: false }).required().trim(),
  country: Joi.string().required().trim().max(33),
  phone: JoiPhoneValidator(false),
});

const ContactForm = () => {
  const { createOrUpdateUser } = useHelperCRM();
  const { openModal: openAlertModal } = useAlertModal();
  const { openModal: openInfoModal } = useInfoModal();

  const {
    registerWithError,
    handleSubmit,
    watch,
    errors,
    setValue,
    reset,
    clearErrors,
    getValues,
  } = useForm<IForm>(formSchema, {
    defaultValues: {
      phone: { number: "", prefix: "+420" },
      email: "",
    },
  });

  const phoneNumber = watch("phone.number");
  const phonePrefix = watch("phone.prefix");

  const handleData: OnFormSubmitType<IForm> = async ({ email, country }) => {
    const userIdReq = await createOrUpdateUser(email, {
      email,
      offers: [],
      phone: phoneNumber.length > 0 ? phonePrefix + phoneNumber : "",
      leadMatterportAcceptFrom: "vizualni_prezentaca",
      leadMatterportLocationProperty: country,
      isLeadMatterport: true,
      consents: [],
    });

    reset();
    if (userIdReq) {
      return openAlertModal({
        animation: "send",
        title: "Úspěšně odesláno. Ozveme se vám.",
        submitText: "Zavřít",
      });
    }

    return openAlertModal({
      animation: "error",
      title:
        "Něco se pokazilo. Zkuste to prosím znovu nebo nás kontaktujte na e-mail podpora@ulovdomov.cz",
      submitText: "Chyba",
    });
  };

  return (
    <CustomWrapper
      id="contact"
      maxW="904px"
      mb={{ base: "48px", md: "80px" }}
      scrollMarginTop="84px"
      contentProps={{
        display: "flex",
        flexDirection: { base: "column", md: "row" },
        borderRadius: "16px",
        p: { base: "16px", md: "64px" },
        backgroundColor: "ud-grey-dark",
        gap: { base: "24px", md: "64px" },
      }}
    >
      <Stack
        sx={{
          "& > p": {
            color: "ud-white",
            textAlign: "left",
            lineHeight: "24px",
          },
        }}
      >
        <Heading variant="h2" color="ud-white">
          Máte zájem o atraktivnější materiály?
        </Heading>
        <Text mt="16px" mb={{ base: "12px", md: "48px" }} fontSize="14px">
          Zanechte nám své kontaktní údaje a my se vám ozveme co nejdříve.
        </Text>
        <Stack flex={1} justify="flex-end">
          <form onSubmit={handleSubmit(handleData)}>
            <Grid
              gridTemplateColumns={{ xl: "1fr 1fr" }}
              gridTemplateRows="auto auto"
              columnGap="24px"
              gridTemplateAreas={{
                base: `
                  "PHONE"
                  "EMAIL"
                  "COUNTRY"
                  "BOTTOM"
                `,
                md: `
                  "PHONE EMAIL"
                  "COUNTRY COUNTRY"
                  "BOTTOM BOTTOM"
                `,
              }}
            >
              <Stack gridArea="PHONE" flex={1} spacing={0}>
                <Text color="ud-white" fontSize="14px">
                  Telefon
                </Text>
                <PhoneInput
                  backgroundColor="ud-white"
                  isLabel={false}
                  error={errors.phone}
                  setValue={setValue}
                  selectBoxValue={phonePrefix}
                  phoneNumberValue={phoneNumber}
                  clearErrors={clearErrors}
                  bgColor="inherit"
                  placeholder="Vaše číslo"
                  sx={{
                    "& section": {
                      backgroundColor: "ud-grey-dark",
                      color: "ud-white",
                      "& p": {
                        color: "ud-white",
                      },
                      "& div div div": {
                        _hover: {
                          backgroundColor: "ud-grey-450 !important",
                        },
                      },
                    },
                    "& placeholder": {
                      color: "ud-grey-500",
                    },
                    "& span": {
                      color: "ud-grey-500",
                    },
                    "& div, div": {
                      borderColor: "ud-grey-500",
                    },
                    "& input": {
                      backgroundColor: "ud-grey-dark",
                      color: "ud-white",
                    },
                    "& svg": {
                      fill: "ud-white",
                    },
                  }}
                />
              </Stack>
              <Box gridArea="EMAIL">
                <Text color="ud-white" fontSize="14px">
                  E-mail *
                </Text>
                <AnimatedInput
                  {...registerWithError("email")}
                  placeholder="Váš E-mail"
                  required
                  sx={{
                    "& placeholder": {
                      color: "ud-grey-500",
                    },
                    "& span": {
                      color: "ud-grey-500",
                    },
                    "& div, div": {
                      borderColor: "ud-grey-500",
                    },
                    "& input": {
                      backgroundColor: "ud-grey-dark",
                      color: "ud-white",
                    },
                  }}
                />
              </Box>
              <Box gridArea="COUNTRY" flex={1}>
                <Text color="ud-white" fontSize="14px">
                  Kde se nemovistost nachází?*
                </Text>
                <LocationSuggest
                  {...registerWithError("country")}
                  sx={{
                    borderWidth: "1px",
                    borderColor: "ud-grey-500",
                    borderStyle: "solid",
                    _focusVisible: {
                      borderWidth: "1px",
                      borderColor: "ud-grey-500",
                      borderStyle: "solid",
                    },
                    _hover: {
                      borderWidth: "1px",
                      borderColor: "ud-grey-500",
                      borderStyle: "solid",
                    },
                  }}
                  placeholder="Obec"
                  variant="DARK"
                  locationValue={getValues("country")}
                  onLocationSelect={(loc) => {
                    clearErrors("country");
                    setValue("country", loc);
                  }}
                  onValueChange={(loc) => {
                    clearErrors("country");
                    setValue("country", loc);
                  }}
                  onClear={() => {
                    setValue("country", "");
                  }}
                />
                {errors.country && (
                  <Text color="ud-primary" fontSize="12px">
                    {errors.country.message}
                  </Text>
                )}
              </Box>
              <Stack
                gridArea="BOTTOM"
                flex={1}
                spacing={0}
                mt="8px"
                display="grid"
                gap={{ base: "4px", md: "0" }}
                gridTemplateAreas={{
                  base: `
                  "NOTIFICATION"
                  "NEWS"
                  "SUBMIT"
                `,
                  md: `
                  "NOTIFICATION NOTIFICATION SUBMIT"
                  "NEWS NEWS SUBMIT"
                `,
                }}
                gridTemplateColumns={{ md: "1fr auto" }}
                gridTemplateRows={{ md: "auto auto" }}
              >
                <Text fontSize="12px" color="ud-white" gridArea="NEWS">
                  Odesláním formuláře souhlasíte se{" "}
                  <Text
                    as="span"
                    fontSize="12px"
                    color="ud-white"
                    textDecoration="underline"
                    cursor="pointer"
                    onClick={() =>
                      openInfoModal({
                        title: "Zpracování osobních údajů",
                        content: (
                          <>
                            <Text>
                              Vaše osobní údaje pečlivě chráníme a nebudeme je
                              nikomu dalšímu poskytovat. Vaše osobní údaje
                              (jméno, telefon, e-mail a údaje o nemovitosti)
                              budeme my, tedy společnost UlovDomov.cz s.r.o.,
                              IČO: 293 00 631, se sídlem Lazaretní 925/9, 615 00
                              Brno, zpracovávat pouze za účelem konzultace
                              prodeje vaší nemovitosti a informování vás o
                              našich službách a produktech, v souladu s platnou
                              legislativou a zásadami ochrany osobních údajů.
                              Svůj souhlas se zpracováním osobních údajů můžete
                              kdykoliv odvolat – stačí nás kontaktovat na{" "}
                              <CustomLink
                                href="mailto:info@ulovdomov.cz"
                                sx={{
                                  textDecoration: "underline",
                                }}
                                textDecoration="underline"
                                color="ud-gray-300"
                              >
                                info@ulovdomov.cz
                              </CustomLink>
                              . Podrobnější informace a o ochraně osobních údajů
                              naleznete{" "}
                              <CustomLink
                                to="/zpracovani-osobnich-udaju"
                                target="_blank"
                                sx={{
                                  textDecoration: "underline",
                                }}
                                color="ud-gray-300"
                              >
                                zde
                              </CustomLink>
                              .
                            </Text>
                          </>
                        ),
                      })
                    }
                  >
                    zpracováním osobních údajů
                  </Text>
                </Text>
                <Button
                  type="submit"
                  width="fit-content"
                  borderRadius="full"
                  justifySelf={{ base: "flex-end", md: "center" }}
                  px="24px"
                  gridArea="SUBMIT"
                  bg="linear-gradient(86deg, #E74C3C 0%, #E98540 100%)"
                  _focus={{
                    bg: "linear-gradient(86deg, #b72211 0%, #f0701b 100%)",
                  }}
                  _hover={{
                    bg: "linear-gradient(86deg, #b72211 0%, #f0701b 100%)",
                  }}
                >
                  Odeslat
                </Button>
              </Stack>
            </Grid>
          </form>
        </Stack>
      </Stack>
      <Stack
        flex={1}
        borderRadius="12px"
        bgColor="ud-grey-600"
        p="40px"
        align="center"
        justify="center"
        spacing={0}
        sx={{
          "& > p": {
            color: "ud-white",
            textAlign: "center",
            lineHeight: "24px",
          },
        }}
      >
        <Avatar
          boxSize="180px"
          name="Petr Koláček"
          src="/img/scenes/Matterport/kolacek.png"
        />
        <Text pt="32px" fontWeight="bold">
          Petr Koláček
        </Text>
        <CustomLink textColor="ud-white" href="tel:+773128704">
          +420 773 128 704
        </CustomLink>
        <CustomLink
          textColor="ud-white"
          href="mailto:kolacek@ulovdomov.cz"
          textDecoration="underline"
        >
          kolacek@ulovdomov.cz
        </CustomLink>
      </Stack>
    </CustomWrapper>
  );
};
export default ContactForm;
